<template>
  <div class="d-flex align-items-center justify-content-center min-vh-100 bg-white" style="padding: 40px 0;">
    <main class="form-signup w-100 m-auto" style="max-width: 400px; padding: 1rem;">
      <form @submit.prevent="handleSignup">
        <div class="text-center mb-4">
          <h1 class="h3 mb-3 fw-normal">{{ $t('auth.registerTitle') }}</h1>
          <p class="text-muted">
            {{ $t('auth.hasAccount') }} 
            <router-link :to="getRoutePath('/signin')">{{ $t('auth.signInNow') }}</router-link>
          </p>
        </div>

        <button 
          type="button" 
          @click="handleGoogleSignup"
          class="w-100 btn btn-lg btn-outline-secondary d-flex align-items-center justify-content-center mb-3"
          :disabled="loading"
        >
          <svg class="me-2" width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {{ $t('auth.googleSignUp') }}
        </button>

        <div class="text-center my-3">
          <span class="text-muted small">{{ $t('auth.orSignUpWithEmail') }}</span>
        </div>

        <div class="form-floating mb-2">
          <input 
            v-model="form.fullName"
            id="fullName" 
            name="fullName" 
            type="text" 
            autocomplete="name" 
            required 
            class="form-control" 
            :placeholder="$t('auth.namePlaceholder')"
          >
          <label for="fullName">{{ $t('auth.name') }}</label>
        </div>


        <div class="form-floating mb-2">
          <input 
            v-model="form.email"
            id="email" 
            name="email" 
            type="email" 
            autocomplete="email" 
            required 
            class="form-control" 
            :placeholder="$t('auth.emailPlaceholder')"
          >
          <label for="email">{{ $t('auth.email') }}</label>
        </div>

        <div class="form-floating mb-2">
          <input 
            v-model="form.password"
            id="password" 
            name="password" 
            type="password" 
            autocomplete="new-password" 
            required 
            minlength="8" 
            class="form-control" 
            :placeholder="$t('auth.passwordPlaceholder')"
          >
          <label for="password">{{ $t('auth.password') }}</label>
        </div>

        <div class="form-floating mb-3">
          <input 
            v-model="form.confirmPassword"
            id="confirmPassword" 
            name="confirmPassword" 
            type="password" 
            autocomplete="new-password" 
            required 
            class="form-control" 
            :class="{ 'is-invalid': passwordMismatch }"
            :placeholder="$t('auth.confirmPasswordPlaceholder')"
          >
          <label for="confirmPassword">{{ $t('auth.confirmPassword') }}</label>
          <div v-if="passwordMismatch" class="invalid-feedback">
            {{ $t('auth.passwordMismatch') }}
          </div>
        </div>

        <div class="form-check text-start my-3">
          <input 
            v-model="form.terms"
            id="terms" 
            name="terms" 
            type="checkbox" 
            required 
            class="form-check-input"
          >
          <label for="terms" class="form-check-label">
            {{ $t('auth.agreeToTerms') }} 
            <router-link :to="getRoutePath('/terms')">{{ $t('auth.termsOfService') }}</router-link> {{ $t('auth.and') }} 
            <router-link :to="getRoutePath('/privacy')">{{ $t('auth.privacyPolicy') }}</router-link>
          </label>
        </div>

        <div class="form-check text-start mb-3">
          <input 
            v-model="form.marketing"
            id="marketing" 
            name="marketing" 
            type="checkbox" 
            class="form-check-input"
          >
          <label for="marketing" class="form-check-label">
            {{ $t('auth.acceptMarketing') }}
          </label>
        </div>

        <button 
          type="submit" 
          class="w-100 btn btn-lg btn-primary" 
          :disabled="loading || passwordMismatch"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          {{ loading ? $t('auth.registering') : $t('auth.registerTitle') }}
        </button>

        <div v-if="error" class="alert alert-danger mt-3">
          <p class="mb-0">{{ error }}</p>
        </div>
      </form>
    </main>
  </div>

  <!-- 註冊成功：email 驗證提示 Modal -->
  <div
    v-if="showVerificationModal"
    class="modal d-block"
    tabindex="-1"
    style="background-color: rgba(0,0,0,0.5);"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center py-4">
          <i class="fas fa-envelope-open-text text-primary mb-3" style="font-size: 2.5rem;"></i>
          <h5 class="mb-2">{{ $t('auth.verificationModal.title') }}</h5>
          <p class="text-muted mb-4">{{ $t('auth.verificationModal.message') }}</p>
          <button type="button" class="btn btn-primary px-4" @click="goToApp">
            {{ $t('auth.verificationModal.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useRoutePath } from '../utils/routing'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const getRoutePath = useRoutePath() // 智能路由生成：根路徑返回 '/signin'，多語言返回 '/{lang}/signin'
const authStore = useAuthStore()

// Form state
const form = ref({
  fullName: '',
  email: '',
  password: '',
  confirmPassword: '',
  terms: false,
  marketing: false
})

const loading = ref(false)
const error = ref('')
const showVerificationModal = ref(false)

const passwordMismatch = computed(() => {
  return form.value.confirmPassword && form.value.password !== form.value.confirmPassword
})

/**
 * Firebase 錯誤訊息對應
 */
const getFirebaseErrorMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    'auth/email-already-in-use': t('auth.errors.emailAlreadyInUse'),
    'auth/weak-password': t('auth.errors.weakPassword'),
    'auth/invalid-email': t('auth.errors.invalidEmail'),
    'auth/too-many-requests': t('auth.errors.tooManyRequests'),
    'auth/network-request-failed': t('auth.errors.networkError'),
    'auth/popup-closed-by-user': t('auth.errors.popupClosedByUser'),
    'auth/popup-blocked': t('auth.errors.popupBlocked'),
    'auth/operation-not-allowed': t('auth.errors.operationNotAllowed')
  }
  
  return errorMessages[errorCode] || t('auth.errors.registrationFailed')
}

const handleSignup = async () => {
  if (passwordMismatch.value) return

  loading.value = true
  error.value = ''

  try {
    const result = await authStore.register(
      form.value.email,
      form.value.password,
      form.value.fullName
    )
    
    if (result.success) {
      showVerificationModal.value = true
    } else {
      error.value = result.error || t('auth.errors.registrationFailed')
    }
    
  } catch (err: any) {
    error.value = t('auth.errors.checkInformation')
  } finally {
    loading.value = false
  }
}

const goToApp = () => {
  window.location.href = '/app/organization'
}

/**
 * Google 註冊處理
 * 使用 Auth Store 的 signInWithGoogle 方法確保 Firestore 整合
 */
const handleGoogleSignup = async () => {
  loading.value = true
  error.value = ''

  try {
    console.log('🔐 嘗試 Google 註冊')
    
    // 使用 Auth Store 的 Google 登入方法，會自動處理 Firestore 整合
    const result = await authStore.signInWithGoogle()
    
    if (result.success) {
      console.log('✅ SSG Google 註冊成功，包含 Firestore 整合')
      
      // 註冊成功，直接跳轉到 SPA 應用
      console.log('🔄 重定向到 SPA 儀表板')
      window.location.href = '/app'
    } else {
      error.value = result.error || t('auth.errors.googleSignUpFailed')
    }
    
  } catch (err: any) {
    console.error('❌ SSG Google 註冊失敗:', err)
    
    if (err.code) {
      // Firebase Auth 錯誤
      error.value = getFirebaseErrorMessage(err.code)
    } else {
      // 一般錯誤
      error.value = err.message || t('auth.errors.googleSignUpFailed')
    }
  } finally {
    loading.value = false
  }
}
</script>